import { useEffect, useRef, useState, useCallback } from "react";
import posthog from "posthog-js";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { calculateDistance } from "@/hooks/useLocationSearch";
import { usePromptQueue, PromptPriority } from "@/contexts/PromptQueueContext";
import { PostCheckInVibeDialog } from "@/components/PostCheckInVibeDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const FOLLOWUP_DELAY_MS = 2.5 * 60 * 60 * 1000; // 2h 30m
const FOLLOWUP_GRACE_MS = 30 * 60 * 1000; // 30 min late grace
const PROXIMITY_THRESHOLD_MILES = 0.12; // ~200m
const STORAGE_KEY = "grounded_followup_target";
const HANDLED_KEY = "grounded_followup_handled_ids";
const PROMPT_ID = "followup-vibe-prompt";

/**
 * Analytics only: emits `vibe_report_prompt_shown` once per follow-up CTA that
 * is actually surfaced to the user. Renders nothing.
 */
function FollowupCtaShownTracker({
  visible,
  checkInId,
  cafeId,
}: { visible: boolean; checkInId: string; cafeId: string }) {
  const shownForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!visible) return;
    if (shownForRef.current === checkInId) return;
    shownForRef.current = checkInId;
    try {
      posthog.capture("vibe_report_prompt_shown", {
        source: "followup",
        check_in_id: checkInId,
        cafe_id: cafeId,
      });
    } catch { /* ignore */ }
  }, [visible, checkInId, cafeId]);
  return null;
}



interface FollowupTarget {
  checkInId: string;
  cafeId: string;
  cafeName: string;
  cafeSlug: string | null;
  cafeLat: number;
  cafeLng: number;
  checkedInAt: string; // ISO
}

function readTarget(): FollowupTarget | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FollowupTarget) : null;
  } catch {
    return null;
  }
}

function writeTarget(t: FollowupTarget | null) {
  try {
    if (t) localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
  }
}

function isHandled(id: string): boolean {
  try {
    const raw = localStorage.getItem(HANDLED_KEY);
    if (!raw) return false;
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) && arr.includes(id);
  } catch {
    return false;
  }
}

function markHandled(id: string) {
  try {
    const raw = localStorage.getItem(HANDLED_KEY);
    const arr: string[] = raw ? JSON.parse(raw) : [];
    if (!arr.includes(id)) arr.push(id);
    // Keep list bounded
    const trimmed = arr.slice(-50);
    localStorage.setItem(HANDLED_KEY, JSON.stringify(trimmed));
  } catch {
  }
}

export function FollowupVibePromptProvider() {
  const { user } = useAuth();
  const { requestPrompt, releasePrompt, isActivePrompt } = usePromptQueue();

  const [target, setTarget] = useState<FollowupTarget | null>(null);
  const [showCta, setShowCta] = useState(false);
  const [showVibeDialog, setShowVibeDialog] = useState(false);
  const timerRef = useRef<number | null>(null);
  const firingRef = useRef(false);

  // Hydrate latest qualifying check-in into the target store
  useEffect(() => {
    if (!user) {
      setTarget(null);
      return;
    }

    let cancelled = false;
    (async () => {
      // Look back up to 3h to catch already-eligible check-ins on app open
      const lookbackIso = new Date(
        Date.now() - (FOLLOWUP_DELAY_MS + FOLLOWUP_GRACE_MS)
      ).toISOString();

      const { data, error } = await supabase
        .from("check_ins")
        .select(
          "id, cafe_id, checked_in_at, checked_out_at, followup_checkin_id, cafes:cafe_id ( name, slug, latitude, longitude )"
        )
        .eq("user_id", user.id)
        .eq("is_complete", true)
        .is("followup_checkin_id", null)
        .is("checked_out_at", null)
        .gte("checked_in_at", lookbackIso)
        .order("checked_in_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled || error || !data) return;
      if (isHandled(data.id)) return;

      const cafe: any = data.cafes;
      if (!cafe?.latitude || !cafe?.longitude) return;

      const next: FollowupTarget = {
        checkInId: data.id,
        cafeId: data.cafe_id,
        cafeName: cafe.name,
        cafeSlug: cafe.slug ?? null,
        cafeLat: Number(cafe.latitude),
        cafeLng: Number(cafe.longitude),
        checkedInAt: data.checked_in_at,
      };
      writeTarget(next);
      setTarget(next);
    })();

    // Also pick up persisted target immediately
    const stored = readTarget();
    if (stored && !isHandled(stored.checkInId)) setTarget(stored);

    return () => {
      cancelled = true;
    };
  }, [user]);

  const fire = useCallback(async () => {
    if (firingRef.current || !target) return;
    firingRef.current = true;

    try {
      // Verify check-in is still eligible (no follow-up exists yet, no checkout)
      const { data: row } = await supabase
        .from("check_ins")
        .select("id, checked_out_at")
        .eq("id", target.checkInId)
        .maybeSingle();

      if (!row || row.checked_out_at) {
        markHandled(target.checkInId);
        writeTarget(null);
        setTarget(null);
        return;
      }

      const { data: existing } = await supabase
        .from("check_ins")
        .select("id")
        .eq("followup_checkin_id", target.checkInId)
        .limit(1)
        .maybeSingle();

      if (existing) {
        markHandled(target.checkInId);
        writeTarget(null);
        setTarget(null);
        return;
      }

      // Check proximity
      if (!navigator.geolocation) {
        markHandled(target.checkInId);
        writeTarget(null);
        setTarget(null);
        return;
      }

      const position = await new Promise<GeolocationPosition | null>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (p) => resolve(p),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
        );
      });

      if (!position) {
        markHandled(target.checkInId);
        writeTarget(null);
        setTarget(null);
        return;
      }

      const distMiles = calculateDistance(
        position.coords.latitude,
        position.coords.longitude,
        target.cafeLat,
        target.cafeLng
      );

      if (distMiles >= PROXIMITY_THRESHOLD_MILES) {
        // No longer at the cafe — silently consume the slot
        markHandled(target.checkInId);
        writeTarget(null);
        setTarget(null);
        return;
      }

      // Request a prompt slot and show the CTA
      const granted = requestPrompt(PROMPT_ID, PromptPriority.FOLLOWUP_VIBE);
      if (granted) setShowCta(true);
    } finally {
      firingRef.current = false;
    }
  }, [target, requestPrompt]);

  // Schedule the timer
  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!target) return;
    if (isHandled(target.checkInId)) return;

    const fireAt = new Date(target.checkedInAt).getTime() + FOLLOWUP_DELAY_MS;
    const now = Date.now();
    const delta = fireAt - now;

    if (delta <= 0) {
      // Past due — only fire if within grace window
      if (Math.abs(delta) <= FOLLOWUP_GRACE_MS) {
        timerRef.current = window.setTimeout(() => void fire(), 1000) as unknown as number;
      } else {
        markHandled(target.checkInId);
        writeTarget(null);
        setTarget(null);
      }
      return;
    }

    timerRef.current = window.setTimeout(() => void fire(), delta) as unknown as number;

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [target, fire]);

  const handleAccept = () => {
    setShowCta(false);
    setShowVibeDialog(true);
  };

  const handleDismiss = () => {
    if (target) {
      markHandled(target.checkInId);
      writeTarget(null);
    }
    setShowCta(false);
    releasePrompt(PROMPT_ID);
    setTarget(null);
  };

  const handleVibeDialogChange = (open: boolean) => {
    setShowVibeDialog(open);
    if (!open) {
      if (target) {
        markHandled(target.checkInId);
        writeTarget(null);
      }
      releasePrompt(PROMPT_ID);
      setTarget(null);
    }
  };

  console.log(
    "[followup] showCta:", showCta,
    "isActivePrompt:", isActivePrompt(PROMPT_ID),
    "target:", target?.cafeName
  );

  if (!target) return null;

  return (
    <>
      <FollowupCtaShownTracker
        visible={showCta && isActivePrompt(PROMPT_ID)}
        checkInId={target.checkInId}
        cafeId={target.cafeId}
      />


      <Dialog open={showCta && isActivePrompt(PROMPT_ID)} onOpenChange={(o) => !o && handleDismiss()}>
        <DialogContent
          className="max-w-sm z-[200]"
          overlayClassName="z-[199]"
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Still at {target.cafeName}?</DialogTitle>
            <DialogDescription>
              How are the work vibes now? A quick update helps everyone find their spot.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={handleDismiss}>
              Not now
            </Button>
            <Button onClick={handleAccept}>Update vibes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PostCheckInVibeDialog
        open={showVibeDialog}
        onOpenChange={handleVibeDialogChange}
        cafeId={target.cafeId}
        cafeName={target.cafeName}
        cafeSlug={target.cafeSlug ?? undefined}
        followupOfCheckInId={target.checkInId}
      />
    </>
  );
}
